import React, { useEffect, useState, FocusEvent } from 'react';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { useDebounce } from 'use-debounce';
import SvgBasket from '@components/icons/Basket.tsx';
import { cn } from '@utils/cn.ts';
import { IconWrapper } from '@components/icons/IconWrapper.tsx';
import { formatAmountPlain, formatUnit } from '@utils/formats.ts';
import { useTranslation } from 'react-i18next';
import { UnitType } from '@api/generated/search-service';

interface CounterProps {
  quantity: number | undefined;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  minimumQuantity?: number;
  unit?: UnitType;
  onlyMultiply: boolean;
  isAbsolute?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  variant?: 'default' | 'button';
  disabled: boolean;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

const Counter: React.FC<CounterProps> = ({
  quantity,
  onQuantityChange,
  onRemove,
  minimumQuantity = 1,
  unit,
  onlyMultiply,
  isAbsolute = true,
  size = 'small',
  className,
  variant = 'default',
  disabled = false,
}) => {
  const { t } = useTranslation('client');
  const [internalQuantity, setInternalQuantity] = useState(quantity);
  const [debouncedValue] = useDebounce(internalQuantity, 700);
  const [manualMode, setManualMode] = useState(false);
  const [manualValue, setManualValue] = useState(`${quantity}`);

  useEffect(() => {
    setInternalQuantity(quantity);
  }, [quantity]);

  useEffect(() => {
    if (isNotNullOrUndefined(debouncedValue) && debouncedValue !== quantity) {
      onQuantityChange(debouncedValue);
    }
  }, [debouncedValue]);

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (variant === 'default') {
      e.stopPropagation();
    }

    if ((internalQuantity ?? 0) > minimumQuantity) setInternalQuantity(onlyMultiply ? minimumQuantity : 1);
    else {
      setInternalQuantity(minimumQuantity);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (variant === 'default') {
      e.stopPropagation();
    }
    if (!isNaN(value)) {
      if (onlyMultiply) {
        // Pokud je onlyMultiply, hodnota musí být 0 nebo násobek minimumQuantity
        if (value === 0 || (value >= minimumQuantity && value % minimumQuantity === 0)) {
          setInternalQuantity(value);
        }
      } else {
        if (value >= minimumQuantity) {
          setInternalQuantity(value);
        }
      }
    }
  };

  const handleManualInputMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualValue(e.target.value);
  };

  const handleDecrease = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (variant === 'default') {
      e.stopPropagation();
    }
    const step = onlyMultiply || minimumQuantity < 1 ? minimumQuantity : 1;
    const nQ = round3((internalQuantity ?? 0) - step);

    if (nQ > 0) {
      setInternalQuantity(nQ);
    } else {
      onRemove();
    }
  };

  const handleIncrease = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (variant === 'default') {
      e.stopPropagation();
    }
    const step = onlyMultiply || minimumQuantity < 1 ? minimumQuantity : 1;
    setInternalQuantity(round3((internalQuantity ?? 0) + step));
  };

  const onEntryManual = (e: FocusEvent<HTMLInputElement, Element>) => {
    e.stopPropagation();
    e.preventDefault();
    setManualMode(true);
    setManualValue(`${internalQuantity}`);
    e.target.select();
  };

  const onFinishManual = () => {
    const value = parseFloat(manualValue);
    console.log('OnFinishManual', value);

    if (isNaN(value)) {
      setManualMode(false);
      return;
    }

    // Pokud chceš pouze násobky
    if (onlyMultiply) {
      if (value <= minimumQuantity) {
        setInternalQuantity(minimumQuantity);
      } else {
        const multiplier = Math.ceil(value / minimumQuantity);
        const nextMultiple = multiplier * minimumQuantity;
        console.log('onlyMultiply=true', multiplier, nextMultiple);

        setInternalQuantity(nextMultiple);
      }
      setManualMode(false);
      return;
    }

    // Bez násobků – jen minimum
    if (value < minimumQuantity) {
      setInternalQuantity(minimumQuantity);
    } else {
      setInternalQuantity(value);
    }

    setManualMode(false);
  };

  if (isNullOrUndefined(internalQuantity)) {
    // Variant 'button' - velké tlačítko s textem "Do košíku"
    if (variant === 'button') {
      return (
        <button className="h-[54px] px-6 bg-surface-dark-primary rounded-[999px] inline-flex justify-center items-center gap-2" onClick={handleAddToCart}>
          {minimumQuantity === 1 ? (
            <div className="justify-center text-white text-base font-bold font-brand leading-none">{t('basket.addToCart')}</div>
          ) : (
            <div className="justify-center text-white text-base font-bold font-brand leading-none text-nowrap">{t('basket.addToCartMin', { count: minimumQuantity, unit: unit ? formatUnit(unit) : formatUnit('KUS') })}</div>
          )}

          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.99634 18C6.89634 18 6.00634 18.9 6.00634 20C6.00634 21.1 6.89634 22 7.99634 22C9.09634 22 9.99634 21.1 9.99634 20C9.99634 18.9 9.09634 18 7.99634 18ZM17.9963 18C16.8963 18 16.0063 18.9 16.0063 20C16.0063 21.1 16.8963 22 17.9963 22C19.0963 22 19.9963 21.1 19.9963 20C19.9963 18.9 19.0963 18 17.9963 18ZM16.5463 13C17.2963 13 17.9563 12.59 18.2963 11.97L21.8763 5.48C22.2463 4.82 21.7663 4 21.0063 4H6.20634L5.26634 2H1.99634V4H3.99634L7.59634 11.59L6.24634 14.03C5.51634 15.37 6.47634 17 7.99634 17H19.9963V15H7.99634L9.09634 13H16.5463ZM7.15634 6H19.3063L16.5463 11H9.52634L7.15634 6Z"
                fill="white"
              />
            </svg>
          </div>
        </button>
      );
    }

    // Variant 'default' - ikona košíku
    return (
      <div className={`${isAbsolute ? 'absolute' : ''} right-0 bottom-0`}>
        <div
          className={cn({
            'bg-gray-50 rounded-[999px] inline-flex justify-between items-center': true,
            'h-[32px] px-2 ': size === 'small',
            'h-[40px] px-3': size === 'medium',
            'h-[52px] px-4': size === 'large',
          })}
        >
          <button
            className={cn('bg-surface-dark-primary inline-flex text-white rounded-[999px] justify-center items-center', {
              'w-10 h-10 ': size === 'large',
              'w-8 h-8 ': size === 'medium',
              'w-6 h-6 ': size === 'small',
            })}
            onClick={e => handleAddToCart(e)}
          >
            <IconWrapper
              className={cn({
                'w-7 h-7 ': size === 'large',
                'w-6 h-6 ': size === 'medium',
                'w-4 h-4 ': size === 'small',
              })}
            >
              <SvgBasket />
            </IconWrapper>
          </button>
        </div>
      </div>
    );
  }

  // Variant 'button' - plná šířka s většími tlačítky
  if (variant === 'button') {
    return (
      <div className={cn('w-full h-[54px] px-2 bg-gray-50 rounded-[999px] inline-flex justify-between items-center', className)}>
        <button className="w-10 h-10 bg-white rounded-[999px] flex justify-center items-center" onClick={handleDecrease} disabled={disabled}>
          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H5V11H19V13Z" className="fill-primary" />
            </svg>
          </div>
        </button>

        <input
          className="flex-1 w-[40px]  h-full bg-transparent outline-none text-center border-none text-slate-950 text-sm font-medium leading-[14px]"
          value={manualMode === true ? manualValue : formatAmountPlain(internalQuantity)}
          onChange={manualMode === true ? handleManualInputMode : handleInputChange}
          onBlur={() => onFinishManual()}
          onFocus={e => onEntryManual(e)}
          onKeyUp={e => e.key === 'Enter' && onFinishManual()}
          onClick={e => e.stopPropagation()}
          disabled={disabled}
        />

        <button className="w-10 h-10 bg-surface-dark-primary rounded-[999px] flex justify-center items-center" onClick={handleIncrease} disabled={disabled}>
          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white" />
            </svg>
          </div>
        </button>
      </div>
    );
  }

  // Variant 'default' - kompaktní s různými velikostmi
  return (
    <div className={`${isAbsolute ? 'absolute' : ''} right-0 bottom-0`}>
      <div
        className={cn('bg-gray-50 rounded-full inline-flex justify-between items-center', {
          'w-[109px] h-[40px] px-2 ': size === 'medium',
          'w-[89px] h-[32px] px-2 ': size === 'small',
          'w-[129px] h-[52px] px-3 ': size === 'large',
        })}
      >
        <button className="w-6 h-6 bg-white rounded-full flex justify-center items-center" onClick={handleDecrease} disabled={disabled}>
          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H5V11H19V13Z" className="fill-primary" />
            </svg>
          </div>
        </button>
        <input
          className="flex-1 w-[40px] h-full bg-transparent outline-none text-center border-none text-slate-950 text-sm font-medium leading-[14px]"
          value={manualMode === true ? manualValue : formatAmountPlain(internalQuantity)}
          onChange={manualMode === true ? handleManualInputMode : handleInputChange}
          onBlur={() => onFinishManual()}
          onFocus={e => onEntryManual(e)}
          onKeyUp={e => e.key === 'Enter' && onFinishManual()}
          onClick={e => e.stopPropagation()}
          disabled={disabled}
        />
        <button className="w-6 h-6 bg-surface-dark-primary rounded-full flex justify-center items-center" onClick={handleIncrease} disabled={disabled}>
          <div data-svg-wrapper data-style="Outlined" className="relative">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="white" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Counter;
